import { Screenshot } from '../../../../api/core'
import { Game } from '../../../../features/game/types/Game'
import { ScreenshotSorter } from '../../../../features/implementation-examples/util/comparators'
import { InternalFeatureMap, InternalFeatureMapEntry } from '../../../api/features'
import { InternalGameScreenshot } from '../../../api/screenshot-links'
import { toHtmlEditorImageUrl } from '../../editorial-cards/helpers/editorialCardHelpers'
import { ImplementationGameRow, ImplementationScreenshotCounts, ImplementationScreenshotCountsMap } from '../types/ImplementationExamples'

export const implementationGameListFields = [
  'name',
  'artist',
  'gpsPerMarket',
  'icon',
  'icons',
  'stage',
  'internal',
  'appId',
  'stores',
  'markets',
  'lastAnalysisAt',
  'latestAnalysis',
  'sranks',
  'sdranks',
]

// The backend stores 2000 for "not in the top 2000"; 0 isn't a rank either.
const unrankedRank = 2000

export const getMarketRank = (ranks: Record<string, number> | null | undefined, marketIso: string) => {
  const rank = ranks?.[marketIso]
  return typeof rank === 'number' && rank > 0 && rank < unrankedRank ? rank : undefined
}

export const toImplementationGameRows = (counts: ImplementationScreenshotCountsMap, games: Game[]): ImplementationGameRow[] =>
  games
    .filter((game) => !!game?.id && game.id in counts)
    .map((stored) => {
      const game = new Game(stored)
      const gameCounts = counts[game.id]
      return { id: game.id, game, screenshots: gameCounts?.screenshots ?? 0, comments: gameCounts?.comments ?? 0 }
    })

export const filterImplementationGames = (rows: ImplementationGameRow[], term: string) => {
  const needle = term.trim().toLowerCase()
  if (!needle) return rows
  return rows.filter(({ game }) => [game.name, game.artist].some((value) => (value ?? '').toLowerCase().includes(needle)))
}

export const getScreenshotThumbnailUrl = (url: string) => toHtmlEditorImageUrl(url)

const marketIndex = (screenshot: InternalGameScreenshot, marketIso: string) =>
  (screenshot.markets ?? []).findIndex((iso) => iso.toLowerCase() === marketIso.toLowerCase())

export const isScreenshotInMarket = (screenshot: InternalGameScreenshot, marketIso: string) => marketIndex(screenshot, marketIso) >= 0

// `analysisIds` pairs with `markets` by position.
export const getScreenshotAnalysisId = (screenshot: InternalGameScreenshot, marketIso: string) => {
  const index = marketIndex(screenshot, marketIso)
  return index >= 0 ? screenshot.analysisIds?.[index] || undefined : undefined
}

export const hasScreenshotComment = (screenshot: InternalGameScreenshot) => !!screenshot.commentId

export const countMarketScreenshots = (screenshots: InternalGameScreenshot[], marketIso: string): ImplementationScreenshotCounts => {
  const inMarket = screenshots.filter((screenshot) => isScreenshotInMarket(screenshot, marketIso))
  return { screenshots: inMarket.length, comments: inMarket.filter(hasScreenshotComment).length }
}

const toSortable = ({ url, versionReleaseDate }: InternalGameScreenshot): Screenshot => ({
  url,
  features: {},
  conceptIds: [],
  versionReleaseDate: versionReleaseDate ?? undefined,
})

export const sortScreenshotsNewestFirst = (screenshots: InternalGameScreenshot[]) =>
  [...screenshots].sort((a, b) => ScreenshotSorter(toSortable(a), toSortable(b)))

export type FeatureSectionScreenshot = { screenshot: InternalGameScreenshot; choice?: string }

export type FeatureSection = { feature: InternalFeatureMapEntry; screenshots: FeatureSectionScreenshot[] }

type FeatureSectionOptions = {
  screenshots: InternalGameScreenshot[]
  featureMap: InternalFeatureMap
  marketIso: string
  featureSearch?: string
  commentedOnly?: boolean
}

// Features the market doesn't have are left out, as in legacy.
export const getFeatureSections = ({
  screenshots,
  featureMap,
  marketIso,
  featureSearch = '',
  commentedOnly = false,
}: FeatureSectionOptions): FeatureSection[] => {
  const featuresByLegacyId = new Map(Object.values(featureMap).map((feature) => [String(feature.legacyId), feature]))
  const needle = featureSearch.trim().toLowerCase()
  const shown = sortScreenshotsNewestFirst(
    screenshots.filter((screenshot) => isScreenshotInMarket(screenshot, marketIso) && (!commentedOnly || hasScreenshotComment(screenshot)))
  )
  const sections = new Map<string, FeatureSection>()
  shown.forEach((screenshot) => {
    Object.entries(screenshot.features ?? {}).forEach(([legacyId, weightId]) => {
      const feature = featuresByLegacyId.get(legacyId)
      if (!feature || (needle && !feature.name.toLowerCase().includes(needle))) return
      const section = sections.get(legacyId) ?? { feature, screenshots: [] }
      section.screenshots.push({ screenshot, choice: feature.choices?.find((choice) => choice.weightId === weightId)?.choice })
      sections.set(legacyId, section)
    })
  })
  return Array.from(sections.values()).sort((a, b) => a.feature.name.localeCompare(b.feature.name))
}
